import { useState } from 'react';
import type { ReactElement } from 'react';
import { assetToast, formatDate } from '@xdocs/i18n';
import type { Share, ShareRole, ShareVisibility } from '@xdocs/schemas';
import { Badge, Button, Chip, ConfirmDialog, Input, Modal, Select, SegmentedControl, cn, useI18n, useToast } from '@xdocs/ui';
import { buildDiscoverUrl, buildLinkUrl } from './referralUrl.js';
import { useShare } from './useShare.js';
import { useSharingT } from './useSharingT.js';
import styles from './ShareDialog.module.css';

export interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  documentTitle: string;
  /** The document's current primary share row, when the caller already
   * knows it (e.g. a `/shares` row click) — see `useShare.ts`'s doc comment
   * for why a dialog opened cold from a document can't discover this
   * itself. Omit to start at `'private'`. */
  initialShare?: Share | null;
  /** `visibility='user'` grants already known for this document (same
   * caveat as `initialShare`). */
  initialGrants?: readonly Share[];
}

const LINK_EXPIRY_CHOICES = [
  { value: 'never', days: null },
  { value: '1', days: 1 },
  { value: '7', days: 7 },
  { value: '30', days: 30 },
] as const;

function addDays(days: number): Date {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
}

/**
 * F27/F62/F63/F64: the ONE share dialog, four visibility modes. Self-
 * contained + exported — the Integrate stage mounts it from the editor/
 * explorer/personas "Share" actions (see this dir's `WIRING.md`).
 */
export function ShareDialog({ open, onOpenChange, documentId, documentTitle, initialShare = null, initialGrants = [] }: ShareDialogProps): ReactElement {
  const { locale, t: tCommon } = useI18n();
  const { t } = useSharingT();
  const toast = useToast();
  const share = useShare(documentId, initialShare, initialGrants);

  const currentVisibility: ShareVisibility = share.primary?.visibility ?? 'private';
  const [pendingVisibility, setPendingVisibility] = useState<ShareVisibility>(currentVisibility);
  const [lastSyncedVisibility, setLastSyncedVisibility] = useState<ShareVisibility>(currentVisibility);
  const [grantUserId, setGrantUserId] = useState('');
  const [grantRole, setGrantRole] = useState<ShareRole>('reader');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [revokeLinkConfirmOpen, setRevokeLinkConfirmOpen] = useState(false);
  const [removeGrantId, setRemoveGrantId] = useState<string | null>(null);

  const visibility = share.isPending ? currentVisibility : pendingVisibility;
  // `error.code === 'CONTENT_POLICY_VIOLATION'` alone doesn't narrow the
  // `ErrorAlert` union (the plain `userAlertSchema` member's `code` is ALSO
  // typed to allow that literal) — `violatedTerms` only exists on the real
  // `ContentPolicyAlert` member, so check for the field instead.
  const contentPolicyError = share.error && 'violatedTerms' in share.error ? share.error : null;

  // Keep the picker's pending selection in sync with the last COMMITTED
  // visibility once a mutation actually lands (e.g. right after `Publish`)
  // — otherwise the footer's commit button would linger forever, offering
  // to "commit" a transition that already happened. React's "adjusting
  // state when a prop changes" pattern (setState during RENDER, not an
  // effect — avoids the extra render-then-effect-then-render cascade).
  if (currentVisibility !== lastSyncedVisibility) {
    setLastSyncedVisibility(currentVisibility);
    setPendingVisibility(currentVisibility);
  }

  async function copy(key: string, value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 2000);
    } catch {
      toast.push({ tone: 'error', message: t('sharing:toast.publishFailed') });
    }
  }

  async function handleModeCommit(): Promise<void> {
    try {
      if (pendingVisibility === 'public') {
        await share.publishPublic();
        toast.push(assetToast(locale, 'success', t('sharing:toast.published.action'), documentTitle));
      } else if (pendingVisibility === 'link') {
        await share.publishLink();
        toast.push(assetToast(locale, 'success', t('sharing:toast.linkIssued.action'), documentTitle));
      } else if (pendingVisibility === 'private') {
        await share.makePrivate();
        toast.push(assetToast(locale, 'success', t('sharing:toast.privated.action'), documentTitle));
      }
    } catch {
      // `share.error` (from `useShare`) already carries the alert — surfaced
      // inline below (content-policy panel or generic message), no toast
      // duplication.
    }
  }

  async function handleAddGrant(): Promise<void> {
    if (!grantUserId.trim()) return;
    try {
      await share.addGrant(grantUserId.trim(), grantRole);
      toast.push(assetToast(locale, 'success', t('sharing:toast.grantAdded.action'), documentTitle));
      setGrantUserId('');
    } catch {
      // inline `share.error` below.
    }
  }

  async function handleRemoveGrant(): Promise<void> {
    if (!removeGrantId) return;
    await share.removeGrant(removeGrantId);
    toast.push(assetToast(locale, 'success', t('sharing:toast.grantRemoved.action'), documentTitle));
    setRemoveGrantId(null);
  }

  async function handleRevokeLink(): Promise<void> {
    await share.revokeLink();
    toast.push(assetToast(locale, 'success', t('sharing:toast.linkRevoked.action'), documentTitle));
    setRevokeLinkConfirmOpen(false);
  }

  const COMMIT_LABEL: Record<Exclude<ShareVisibility, 'user'>, string> = {
    private: t('sharing:dialog.makePrivate'),
    public: t('sharing:dialog.publish'),
    link: t('sharing:dialog.link.generate'),
  };

  // `user` grants have no single "commit a visibility transition" action —
  // each grant is its own `share.grant` call from the form below — so the
  // footer never offers a commit button for it.
  const showCommit = pendingVisibility !== currentVisibility && pendingVisibility !== 'user';

  const footer = (
    <div className={cn(styles['footer'])}>
      <Button variant="secondary" onClick={() => onOpenChange(false)}>
        {tCommon('common:action.close')}
      </Button>
      {showCommit && (
        <Button variant="primary" state={share.isPending ? 'loading' : 'idle'} onClick={() => void handleModeCommit()}>
          {COMMIT_LABEL[pendingVisibility]}
        </Button>
      )}
    </div>
  );

  return (
    <Modal open={open} onClose={() => onOpenChange(false)} title={t('sharing:dialog.title', { name: documentTitle })} size="lg" scrollableBody footer={footer}>
      <SegmentedControl<ShareVisibility>
        label={t('sharing:dialog.visibility.label')}
        value={visibility}
        onChange={setPendingVisibility}
        options={[
          { value: 'private', label: t('sharing:dialog.visibility.private') },
          { value: 'public', label: t('sharing:dialog.visibility.public') },
          { value: 'link', label: t('sharing:dialog.visibility.link') },
          { value: 'user', label: t('sharing:dialog.visibility.user') },
        ]}
      />

      {visibility === 'private' && <p className={cn(styles['description'])}>{t('sharing:dialog.visibility.private.description')}</p>}

      {visibility === 'public' && (
        <div className={cn(styles['section'])}>
          <p className={cn(styles['description'])}>{t('sharing:dialog.visibility.public.description')}</p>
          {contentPolicyError && (
            <div role="alert" className={cn(styles['policyPanel'])}>
              <p className={cn(styles['policyTitle'])}>{t('sharing:dialog.policy.title')}</p>
              <p className={cn(styles['description'])}>{contentPolicyError.message}</p>
              <div className={cn(styles['policyChips'])}>
                {contentPolicyError.violatedTerms.map((term) => (
                  <Chip key={term}>{t(`sharing:policyCategory.${term}`)}</Chip>
                ))}
              </div>
              <a href={contentPolicyError.policyUrl} target="_blank" rel="noreferrer" className={cn(styles['policyLink'])}>
                {t('sharing:dialog.policy.terms')}
              </a>
            </div>
          )}
          {!contentPolicyError && share.primary?.visibility === 'public' && share.primary.moderationStatus === 'pending' && (
            <div className={cn(styles['notice'])}>
              <p className={cn(styles['noticeTitle'])}>{t('sharing:dialog.moderation.pending.title')}</p>
              <p className={cn(styles['description'])}>{t('sharing:dialog.moderation.pending.description')}</p>
            </div>
          )}
          {!contentPolicyError && share.primary?.visibility === 'public' && share.primary.moderationStatus === 'approved' && (
            <div className={cn(styles['notice'])}>
              <p className={cn(styles['noticeTitle'])}>{t('sharing:dialog.moderation.approved.title')}</p>
              <p className={cn(styles['description'])}>{t('sharing:dialog.moderation.approved.description')}</p>
              <div className={cn(styles['linkRow'])}>
                <Input label={t('sharing:dialog.referral.title')} readOnly value={buildDiscoverUrl(share.primary.id)} />
                <Button variant="secondary" onClick={() => void copy('referral', buildDiscoverUrl(share.primary?.id ?? ''))}>
                  {copiedKey === 'referral' ? t('sharing:dialog.link.copied') : t('sharing:dialog.link.copy')}
                </Button>
              </div>
              <p className={cn(styles['description'])}>{t('sharing:dialog.referral.description')}</p>
            </div>
          )}
          <div className={cn(styles['notice'])}>
            <p className={cn(styles['noticeTitle'])}>{t('sharing:dialog.reward.title')}</p>
            <p className={cn(styles['description'])}>{t('sharing:dialog.reward.description')}</p>
          </div>
        </div>
      )}

      {visibility === 'link' && (
        <div className={cn(styles['section'])}>
          <p className={cn(styles['description'])}>{t('sharing:dialog.visibility.link.description')}</p>
          {share.primary?.visibility === 'link' && !share.primary.linkRevokedAt ? (
            <>
              {share.primary.linkToken && (
                <div className={cn(styles['linkRow'])}>
                  <Input label={t('sharing:dialog.link.title')} readOnly value={buildLinkUrl(share.primary.linkToken)} />
                  <Button variant="secondary" onClick={() => void copy('link', buildLinkUrl(share.primary?.linkToken ?? ''))}>
                    {copiedKey === 'link' ? t('sharing:dialog.link.copied') : t('sharing:dialog.link.copy')}
                  </Button>
                </div>
              )}
              <Select
                label={t('sharing:dialog.link.expiry.label')}
                value={share.primary.linkExpiresAt ? 'custom' : 'never'}
                onChange={(event) => {
                  const choice = LINK_EXPIRY_CHOICES.find((c) => c.value === event.target.value);
                  void share.setLinkExpiry(choice?.days ? addDays(choice.days) : null);
                }}
                options={[
                  { value: 'never', label: t('sharing:dialog.link.expiry.never') },
                  ...LINK_EXPIRY_CHOICES.filter((c) => c.days !== null).map((c) => ({ value: c.value, label: `${c.days}d` })),
                  ...(share.primary.linkExpiresAt ? [{ value: 'custom', label: formatDate(locale, share.primary.linkExpiresAt, { dateStyle: 'medium' }) }] : []),
                ]}
              />
              <Button variant="destructive" onClick={() => setRevokeLinkConfirmOpen(true)}>
                {t('sharing:dialog.link.revoke')}
              </Button>
            </>
          ) : (
            share.primary?.visibility === 'link' &&
            share.primary.linkRevokedAt && (
              <p role="alert" className={cn(styles['description'])}>
                {t('sharing:dialog.link.revoked.notice')}
              </p>
            )
          )}
        </div>
      )}

      {visibility === 'user' && (
        <div className={cn(styles['section'])}>
          <p className={cn(styles['description'])}>{t('sharing:dialog.visibility.user.description')}</p>
          <div className={cn(styles['grantForm'])}>
            <Input label={t('sharing:dialog.grant.userIdLabel')} hint={t('sharing:dialog.grant.userIdHint')} value={grantUserId} onChange={(event) => setGrantUserId(event.target.value)} />
            <Select
              label={t('sharing:dialog.grant.roleLabel')}
              value={grantRole}
              onChange={(event) => setGrantRole(event.target.value as ShareRole)}
              options={[
                { value: 'reader', label: t('sharing:dialog.grant.role.reader') },
                { value: 'editor', label: t('sharing:dialog.grant.role.editor') },
              ]}
            />
            <Button variant="primary" state={share.isPending ? 'loading' : 'idle'} disabled={!grantUserId.trim()} onClick={() => void handleAddGrant()}>
              {t('sharing:dialog.grant.add')}
            </Button>
          </div>
          {share.grants.length === 0 ? (
            <p className={cn(styles['description'])}>{t('sharing:dialog.grant.empty')}</p>
          ) : (
            <ul className={cn(styles['grantList'])}>
              {share.grants.map((grant) => (
                <li key={grant.id} className={cn(styles['grantRow'])}>
                  <span>{grant.sharedWithUserId}</span>
                  <Badge tone="neutral">{grant.shareRole === 'editor' ? t('sharing:dialog.grant.role.editor') : t('sharing:dialog.grant.role.reader')}</Badge>
                  <Button variant="ghost" onClick={() => setRemoveGrantId(grant.id)}>
                    {t('sharing:dialog.grant.remove')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {share.error && !contentPolicyError && (
        <p role="alert" className={cn(styles['errorText'])}>
          {share.error.message}
        </p>
      )}

      <ConfirmDialog
        open={revokeLinkConfirmOpen}
        onOpenChange={setRevokeLinkConfirmOpen}
        action={t('sharing:dialog.link.revoke')}
        assetName={documentTitle}
        destructive
        loading={share.isPending}
        onConfirm={() => void handleRevokeLink()}
      />

      <ConfirmDialog
        open={removeGrantId !== null}
        onOpenChange={(next) => !next && setRemoveGrantId(null)}
        action={t('sharing:dialog.grant.remove')}
        assetName={documentTitle}
        destructive
        loading={share.isPending}
        onConfirm={() => void handleRemoveGrant()}
      />
    </Modal>
  );
}
